import { useRealtime } from "@nwga/features/client";
import { Toaster } from "@nwga/ui/components/sonner";
import {
	createRootRouteWithContext,
	Outlet,
	useRouterState,
} from "@tanstack/react-router";
import * as React from "react";
import { lazy, Suspense } from "react";
import { AppFooter } from "../components/app-footer.tsx";
import { AppHeader } from "../components/app-header.tsx";
import { ErrorState } from "../components/error-state.tsx";
import { env } from "../env.ts";
import type { RouterContext } from "../router.tsx";

/**
 * Devtools, in development only. Browser tests exercise the site's landmarks
 * without the developer panel's extra navigation and footer.
 *
 * The condition is `import.meta.env.DEV` inline rather than a re-exported
 * constant: Vite replaces it with a literal at build time, so the whole branch
 * — and the 60 kB of devtools behind it — is eliminated from the production
 * bundle instead of merely never rendered.
 */
const RouterDevtools =
	import.meta.env.DEV && import.meta.env.MODE !== "test"
		? lazy(async () => {
				const module = await import("@tanstack/react-router-devtools");
				return { default: module.TanStackRouterDevtools };
			})
		: () => null;

export const Route = createRootRouteWithContext<RouterContext>()({
	component: RootLayout,
	errorComponent: ({ error, reset }) => (
		<div className="mx-auto max-w-2xl px-4 py-16">
			<ErrorState error={error} onRetry={reset} />
		</div>
	),
	notFoundComponent: () => (
		<div className="mx-auto max-w-2xl px-4 py-16 text-center">
			<h1 className="text-2xl font-semibold">Page not found</h1>
			<p className="mt-2 text-muted-foreground">
				The page you were looking for does not exist.
			</p>
		</div>
	),
});

function RootLayout() {
	// Opens the SSE connection for the whole session. Events arrive as query
	// invalidations, so nothing below has to know realtime exists.
	useRealtime({ baseUrl: env.apiUrl });

	const mainRef = React.useRef<HTMLElement>(null);
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const previousPath = React.useRef(pathname);

	/**
	 * Move focus to the new page on navigation.
	 *
	 * In a single-page app the browser does none of what it does on a real page
	 * load: focus stays wherever the clicked link was, and a screen reader
	 * announces nothing, so somebody navigating by keyboard has no idea the page
	 * changed. Focusing the main landmark restores both.
	 *
	 * Skipped on first render, where the browser's own load behaviour is correct
	 * and stealing focus would be the wrong thing.
	 */
	React.useEffect(() => {
		if (previousPath.current === pathname) return;
		previousPath.current = pathname;
		mainRef.current?.focus();
	}, [pathname]);

	return (
		<div className="flex min-h-full flex-col">
			{/*
        Visible only when focused, which is the point: a keyboard user lands on
        it first and can jump the header nav, and nobody else ever sees it.
      */}
			<a
				href="#main"
				className="-translate-y-full focus:-translate-y-0 sr-only absolute top-0 left-0 z-50 m-2 rounded-md bg-background px-4 py-2 font-medium text-sm shadow ring-2 ring-ring transition-transform focus:not-sr-only"
			>
				Skip to content
			</a>

			<AppHeader />

			<main
				id="main"
				ref={mainRef}
				// `-1` so it can be focused programmatically and by the skip link, but
				// never lands in the tab order as a stop of its own.
				tabIndex={-1}
				/*
				 * A screen tall at minimum, so the footer is always below the fold.
				 *
				 * `flex-1` alone only pushes the footer to the bottom of the viewport,
				 * which on a short page — a confirmation, an empty search — puts a
				 * dense navy slab of address, socials and county links directly under
				 * two lines of content, and it reads as the end of a page that has
				 * barely begun. Costing a short page one screen of scroll is the
				 * cheaper trade.
				 *
				 * `dvh`, not `vh`: on mobile `vh` is the viewport with the browser
				 * chrome retracted, so it overshoots by the height of the address bar.
				 */
				className="min-h-dvh flex-1 outline-none"
			>
				<Outlet />
			</main>

			<AppFooter />

			<Toaster position="bottom-right" />

			<Suspense fallback={null}>
				<RouterDevtools position="bottom-left" />
			</Suspense>
		</div>
	);
}
